// ---------------------------------------------------------------------------
// One chat line.
//
// Four kinds share one stream, so each kind changes more than one property at
// once: a team line is tinted with its team's colour and led by a `team` label,
// a spectator line recedes onto the alternate surface and is led by
// `spectator`, a system line goes italic and loses its author slot. Colour on
// its own would fail the moment a team colour landed near the surface under it.
//
// Two things here are load bearing.
//
// The tint is an opaque `color-mix`, not a translucent wash. The panel is
// already a surface, and a wash on top of it composites deeper than either
// alone, which is off the bottom of what the palette was solved for. Mixing 16%
// of the team colour into `--c-bg` lands the row between `bg` and `raised` in
// the dark theme and between `bg` and `surface-2` in the light one — surfaces
// the text tokens were solved against.
//
// The author's colour is a swatch and never the name's colour. Team colours run
// from L 0.62 to L 0.80, which reads on the dark background and disappears on
// the light one; `PlayerChip` makes the same choice for the same reason.
//
// Every row carries the same padding whatever its kind, so a tinted row never
// nudges the plain rows above and below it.
// ---------------------------------------------------------------------------

import type { CSSProperties, ReactElement, ReactNode } from 'react';
import type { ChatMessage } from '@countdown/shared';

export interface ChatMessageRowProps {
  message: ChatMessage;
  /**
   * The author's colour: their team's while teams are on, their own otherwise.
   * An OKLCH string, so it can only be applied as an inline style. Null when the
   * author has since left the room — the swatch still occupies its space.
   */
  authorColor?: string | null;
  /**
   * The team's colour, set only for a line that went to a team channel. Its
   * presence is what tints the row.
   */
  teamColor?: string | null;
  /**
   * The host's controls for this line, if this reader has any. Absent for
   * everybody else and absent on a system line, which is the room's own record
   * and not the host's to edit.
   *
   * A slot rather than a set of callbacks, so this component stays a renderer:
   * the one place that knows who the host is, and which of the four powers apply
   * to a line whose author has left, remains the panel. See
   * `ChatModerationMenu`.
   */
  control?: ReactNode;
}

/**
 * Shared by every kind, so no kind is inset differently from its neighbours.
 *
 * `shrink-0` is not decorative: the list is a column flex container, and a flex
 * item in one gives up its own height before the container agrees to scroll. A
 * three-line message would be squeezed to one and clipped.
 *
 * **It is a flex row now, and the text lives one level in.** The host has a
 * control at the end of every line they can act on, and hanging it off the
 * inline flow would have made it wrap into the middle of a sentence. Putting
 * the whole of the old row into a `min-w-0 flex-1` span keeps the inline
 * behaviour byte for byte — the swatch, the tag, the name and the body still
 * wrap together as one paragraph — and gives the button a column that is only
 * there when there is a button. A row with no control renders as it always did.
 */
const ROW =
  'flex shrink-0 items-start gap-1 rounded-tile px-2.5 py-1.5 leading-[1.45] whitespace-normal [overflow-wrap:anywhere]';

/**
 * The text of a row.
 *
 * `min-w-0` is the whole of it, and it is load bearing: a flex item will not
 * shrink below its own content without it, so a long unbroken run of characters
 * would widen the row and take the panel with it. The wrapping rules stay on the
 * row above, because `white-space` and `overflow-wrap` both inherit — one
 * declaration reaching both elements is better than two that can disagree, and
 * it keeps the invariant stated on the element a reader looks at first.
 */
const BODY = 'min-w-0 flex-1';

/**
 * Line breaks are collapsed to spaces rather than preserved, and the reason is
 * the composer: it is an `<input>`, so there is no keystroke in this app that
 * produces a newline. Shift-Enter does nothing in a single-line field, and a
 * text input's value sanitisation strips CR and LF out of a paste before script
 * can see them. A break can therefore only reach a body from something that is
 * not this client, which means "they typed a break, they meant it" is not an
 * argument that applies here — while the argument against is concrete. The
 * panel's height is fixed on purpose, `CHAT_MESSAGE_MAX` is 300, and 300
 * newlines in one body would be a free way to push every other line out of a
 * room's chat.
 *
 * Collapsing also caps the damage absolutely rather than approximately: no body
 * from any source can add a line box, so the tallest possible row is 300
 * characters of wrapped text — around ten lines at the panel's narrowest, which
 * is inside the list's own height.
 *
 * It is applied here as well as in the composer because the row also renders
 * bodies it did not send: system lines, bots, and any future client. If the
 * composer ever becomes a textarea, this is the decision to revisit — swap this
 * for `whitespace-pre-wrap` on `ROW` and add a cap on the line count.
 */
export function singleLine(body: string): string {
  return body.replace(/\s*[\r\n]+\s*/g, ' ').trim();
}

/** Mono uppercase marker: `team`, `spectator`, `masked`. */
function Tag({ children, className }: { children: ReactNode; className?: string }): ReactElement {
  return (
    <span
      className={`font-mono text-[10px] font-semibold uppercase tracking-[0.08em] text-ink-dim ${className ?? ''}`}
    >
      {children}
    </span>
  );
}

/** Fixed size in every state, so a line with no known author still lines up. */
function Swatch({ color }: { color: string | null }): ReactElement {
  return (
    <span
      aria-hidden="true"
      className={`mr-[7px] inline-block h-2 w-2 shrink-0 rounded-full align-[0.08em] ${
        color === null ? 'bg-line-strong' : ''
      }`}
      style={color === null ? undefined : { background: color }}
    />
  );
}

export function ChatMessageRow({
  message,
  authorColor,
  teamColor,
  control,
}: ChatMessageRowProps): ReactElement {
  const body = singleLine(message.body);

  if (message.system) {
    return (
      <li data-chat-kind="system" className={`${ROW} text-[13px] italic text-ink-dim`}>
        <span className={BODY}>{body}</span>
      </li>
    );
  }

  const name = message.authorName ?? 'someone';

  // **A MASKED LINE IS NOT A DELETED ONE, AND THERE IS NO LONGER A DELETED ONE
  // TO CONFUSE IT WITH.** A censored message keeps its body with a run of blocks
  // in it and a `masked` tag beside it, and it comes back at the reveal: it is
  // the game hiding an answer for the length of a round, not the host taking
  // something away. That half is unchanged and must stay.
  //
  // The tombstone that used to be drawn here is gone. Deletion is a removal now
  // — the row leaves the array, on the host's single delete and on a purge alike
  // — so a message that has been taken down never reaches this component at all.
  // The moderation record is the `chat-deleted` entry in `state.log`, which
  // names the host and the author and outlives `CHAT_HISTORY_LIMIT` scrolling
  // the line away; the room's own copy of what happened is not a row in the
  // stream. The one thing that came free with a tombstone was the unread count,
  // which counted a length that could not shrink — see `chatAccess.ts`, which
  // counts from a watermark instead.
  const masked = message.censored ? <Tag className="ml-[7px]">masked</Tag> : null;

  // A spectator's line is quieted for everyone, because their answers never
  // enter scoring and a player skimming chat mid-round should not weigh it the
  // same. The recess is an opaque surface token for the same reason the team
  // tint is opaque.
  if (message.fromSpectator) {
    return (
      <li data-chat-kind="spectator" className={`${ROW} bg-bg-alt text-[13px] text-ink-dim`}>
        <span className={BODY}>
          <Swatch color={authorColor ?? null} />
          <Tag className="mr-[7px]">spectator</Tag>
          <span className="font-semibold">{name}</span> <span>{body}</span>
          {masked}
        </span>
        {control}
      </li>
    );
  }

  const onTeamChannel = message.channel === 'team' && typeof teamColor === 'string';
  const tinted: CSSProperties | undefined = onTeamChannel
    ? {
        backgroundColor: `color-mix(in oklab, ${teamColor} 16%, var(--c-bg))`,
        // An inset rail rather than a border: a border would move the text of a
        // tinted row three pixels off the rows around it.
        boxShadow: `inset 3px 0 0 ${teamColor}`,
      }
    : undefined;

  return (
    <li
      data-chat-kind={onTeamChannel ? 'team' : 'global'}
      className={`${ROW} text-[14px] text-ink`}
      style={tinted}
    >
      <span className={BODY}>
        <Swatch color={authorColor ?? null} />
        {onTeamChannel ? <Tag className="mr-[7px]">team</Tag> : null}
        <span className="font-semibold">{name}</span> <span>{body}</span>
        {masked}
      </span>
      {control}
    </li>
  );
}
